/** Shapes returned by the staff API. Field names mirror the SQL columns. */

export type StaffRole = 'admin' | 'deployment' | 'support';

export const STAFF_ROLE_LABEL: Record<StaffRole, string> = {
  admin: 'Admin',
  deployment: 'Deployment',
  support: 'Support',
};

export const STAFF_ROLE_HELP: Record<StaffRole, string> = {
  admin: 'Everything, including deletes and revokes, product secrets and staff accounts.',
  deployment: 'Create and edit anything, but never delete or revoke.',
  support: 'Read-only, limited to Overview, Companies and Licenses.',
};

export interface Staff {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
}

/** A staff account as listed on the Staff page. */
export interface StaffAccount {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
  is_active: boolean;
  last_login_at: string | null;
  created_at: string;
  updated_at: string;
}

export type CompanyRole = 'superadmin' | 'admin' | 'member';

export const COMPANY_ROLE_LABEL: Record<CompanyRole, string> = {
  superadmin: 'Super admin',
  admin: 'Admin',
  member: 'User',
};

export interface Product {
  id: string;
  code: string;
  name: string;
  description: string | null;
  api_key: string;
  is_active: boolean;
  secret_rotated_at?: string;
  /** True when staff chose the secret rather than Heron generating it. */
  secret_set_by_staff?: boolean;
  license_count?: number;
  created_at: string;
  updated_at: string;
}

export interface ProductCredentials {
  apiKey: string;
  apiSecret: string;
  note: string;
}

export interface CompanySummary {
  id: string;
  code: string;
  name: string;
  contact_person: string | null;
  contact_email: string | null;
  is_active: boolean;
  active_user_count: number;
  license_count: number;
  usable_license_count: number;
  created_at: string;
  updated_at: string;
  /** Names of the external systems this company is synced from. */
  synced_from?: string[];
}

export interface Company {
  id: string;
  code: string;
  name: string;
  contact_person: string | null;
  contact_email: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  synced_from?: string[];
}

export type LicenseStatus = 'active' | 'suspended' | 'revoked';

/**
 * Straight from the `license_overview` view. `is_expired`, `is_usable` and
 * `days_remaining` are computed by Postgres on every read, never stored — so
 * what the console shows and what the login gate enforces cannot drift apart.
 */
export interface License {
  id: string;
  company_id: string;
  product_id: string;
  company_name: string;
  company_code: string;
  company_is_active: boolean;
  product_name: string;
  product_code: string;
  starts_at: string;
  expires_at: string;
  status: LicenseStatus;
  max_users: number | null;
  notes: string | null;
  is_expired: boolean;
  is_usable: boolean;
  days_remaining: number;
  created_at: string;
  updated_at: string;
  /** The external system that sets this license's status and expiry, if any. */
  synced_from?: string | null;
}

export interface CompanyUser {
  id: string;
  username: string;
  email: string | null;
  full_name: string | null;
  company_role: CompanyRole;
  is_company_admin: boolean;
  must_change_password: boolean;
  is_active: boolean;
  has_password: boolean;
  last_login_at: string | null;
  locked_until: string | null;
  created_by_username: string | null;
  created_at: string;
  products: Array<{ id: string; code: string; name: string }>;
  synced_from?: string[];
  account_book_id: string | null;
  account_book_name: string | null;
  /** The book this login opens in each product (product_id null = any product). */
  account_books?: Array<{
    product_id: string | null;
    product_name: string | null;
    book_id: string;
    book_name: string;
  }>;
  /** Products this login is signed in to right now (active in the last 20 min). */
  active_sessions?: Array<{
    product_id: string;
    product_name: string;
    last_active_at: string;
    ip: string | null;
  }>;
}

/** An AutoCount SQL server a company runs. The password is write-only. */
export interface Server {
  id: string;
  company_id: string;
  name: string;
  address: string;
  sql_port: number;
  sql_user: string | null;
  sql_password_set: boolean;
  notes: string | null;
  book_count: number;
  /** The product its books are for; null = any product (set up before products were chosen). */
  product_id: string | null;
  product_name: string | null;
  created_at: string;
  updated_at: string;
}

/** How Heron sends email (Sent email → Email settings). */
export interface EmailSettingsResponse {
  /** 'environment' until someone saves from the console. */
  source: 'console' | 'environment';
  settings: {
    driver: 'console' | 'smtp' | 'resend';
    from: string;
    smtpHost: string | null;
    smtpPort: number;
    smtpSecure: boolean;
    smtpUser: string | null;
    smtpPasswordSet: boolean;
    resendApiKeySet: boolean;
  };
}

/** What a blank server form starts with (Heron's AUTOCOUNT_DEFAULT_SQL_* settings). */
export interface AutocountDefaults {
  sqlUser: string;
  /** A default password exists, so leaving the field blank still stores one. */
  defaultPasswordSet: boolean;
}

/** An AutoCount database (account book) on one of a company's servers. */
export interface AccountBook {
  id: string;
  company_id: string;
  server_id: string;
  server_name: string;
  /** Its server's product; null = any product. */
  product_id: string | null;
  product_name: string | null;
  name: string;
  database_name: string;
  autocount_company_id: string | null;
  login_count: number;
  created_at: string;
  updated_at: string;
}

export interface CompanyDetail {
  company: Company;
  licenses: License[];
  users: CompanyUser[];
  servers: Server[];
  accountBooks: AccountBook[];
}

export interface InviteResult {
  user: { id: string; username: string; email: string };
  products: string[];
  invite: {
    emailStatus: 'sent' | 'failed';
    outboxId: string;
    /** Only present with EMAIL_DRIVER=console. */
    devLink?: string;
  };
  warnings: string[];
}

export interface DashboardTotals {
  active_companies: number;
  active_products: number;
  active_users: number;
  usable_licenses: number;
  expired_licenses: number;
  expiring_soon: number;
}

export interface AttentionRow {
  id: string;
  company_name: string;
  product_name: string;
  expires_at: string;
  days_remaining: number;
  status: LicenseStatus;
  is_expired: boolean;
}

export interface AuditEntry {
  id: number;
  actor_type: 'staff' | 'user' | 'system';
  actor_id: string | null;
  actor_label: string | null;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  company_id: string | null;
  company_name: string | null;
  product_name: string | null;
  metadata: Record<string, unknown>;
  ip: string | null;
  created_at: string;
}

export interface DemoStatus {
  /** False when DEMO_RESET_ENABLED is off — the default in production. */
  enabled: boolean;
  environment: 'development' | 'test' | 'production';
  productCode: string;
  present: boolean;
  productPresent: boolean;
  /** Products other than the demo one — all deleted by a reset. */
  otherProducts: number;
  companies: Array<{
    code: string;
    name: string;
    users: number;
    licenses: number;
    /** False for anything created since the seed. Deleted either way. */
    isDemo: boolean;
  }>;
  totals: { companies: number; users: number; licenses: number; auditEntries: number };
}

export interface DemoResetResult {
  product: {
    code: string;
    name: string;
    apiKey: string;
    /** Only when the product row had to be created; a reset never rotates it. */
    apiSecret?: string;
    secretUnchanged: boolean;
  };
  companies: Array<{
    code: string;
    name: string;
    licenseState: 'active' | 'expired';
    users: Array<{ username: string; password: string; role: 'admin' | 'user' }>;
  }>;
  deleted: {
    companies: number;
    users: number;
    licenses: number;
    products: number;
    auditEntries: number;
    emails: number;
  };
}

export interface OutboxEmail {
  id: string;
  to_email: string;
  subject: string;
  template: string;
  body_text: string;
  status: 'pending' | 'sent' | 'failed';
  error: string | null;
  username: string | null;
  sent_at: string | null;
  created_at: string;
}


// --- external sync sources (/api/staff/sync-sources) ------------------------

export type SyncSourceKind = 'presoft_portal' | 'custom_sql';

export interface SyncStats {
  companies?: { created: number; linked: number; updated: number; unlinked: number };
  licenses?: { created: number; updated: number; revoked: number };
  users?: {
    created: number;
    linked: number;
    updated: number;
    passwordsChanged: number;
    deactivated: number;
    unlinked: number;
  };
  skipped?: Array<{ kind: 'company' | 'login'; ref: string; reason: string }>;
  warnings?: string[];
  source?: { companies: number; logins: number };
}

export interface SyncSource {
  id: string;
  name: string;
  kind: SyncSourceKind;
  product_id: string;
  product_name: string;
  product_code: string;
  companies_sql: string | null;
  logins_sql: string | null;
  interval_seconds: number;
  is_enabled: boolean;
  has_nudge_token: boolean;
  /** Host and database name only — never the credentials. */
  db_target: string;
  company_count: number;
  login_count: number;
  last_run_id: number | null;
  last_run_trigger: string | null;
  last_run_at: string | null;
  last_run_ok: boolean | null;
  last_run_error: string | null;
  last_run_stats: SyncStats | null;
  last_success_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SyncRun {
  id: number;
  trigger: string;
  started_at: string;
  finished_at: string | null;
  ok: boolean | null;
  stats: SyncStats;
  error: string | null;
}

export interface SyncTestResult {
  ok: boolean;
  error?: string;
  companies?: number;
  logins?: number;
  passwordFormats?: Record<string, number>;
  loginsWithoutCompany?: number;
  problems?: string[];
  sample?: {
    companies: Array<{ externalId: string; name: string; status: string; expiresAt: string | null }>;
    logins: Array<{
      externalId: string;
      username: string;
      companyExternalId: string;
      isActive: boolean;
      passwordFormat: string;
    }>;
  };
}

export interface SyncedLicenseRow {
  license_id: string;
  company_id: string;
  company_name: string;
  company_active: boolean;
  source_id: string;
  source_name: string;
  product_name: string;
  status: LicenseStatus;
  expires_at: string;
  is_expired: boolean;
  is_usable: boolean;
  days_remaining: number;
  never_expires: boolean;
  login_count: number;
}

export interface SyncOverview {
  licenses: SyncedLicenseRow[];
  sources: Array<{
    id: string;
    name: string;
    is_enabled: boolean;
    last_run_at: string | null;
    last_run_ok: boolean | null;
    last_run_error: string | null;
    last_success_at: string | null;
    skipped_count: number;
  }>;
}
